/**
 * Shared helpers for the hard-gated lesson views (practice, review, exam):
 * the component alias for virtual types, the answer after a miss (Show me),
 * the listen-line audio lookup, the wrong-answer recap note, and the
 * skip-with-reason list.
 */
import type { PackTaskType, ReviewQueueEntry, SkipReason } from '../types';

/** listen/reply render as Choice, recall and the spelling card (ticket 05) as GapFill; spot it and dictation (ticket 11), sound-alike and say it from the meaning (ticket 14) as themselves. */
export const COMPONENT: Record<PackTaskType, string> = {
  flashcard: 'Flashcard',
  choice: 'Choice',
  gapfill: 'GapFill',
  reorder: 'Reorder',
  sayitback: 'SayItBack',
  listen: 'Choice',
  reply: 'Choice',
  recall: 'GapFill',
  spelling: 'GapFill',
  spotit: 'SpotIt',
  dictation: 'Dictation',
  soundalike: 'SoundAlike',
  sayfrommeaning: 'SayFromMeaning',
};

/**
 * How each component shows its answer after a miss (v1 ticket 32). `shown`:
 * its own screen already shows it (a Choice marks the right option after any
 * pick, a flashcard is graded on its back, a SayItBack's answer is its line).
 * `show-me`: only after a right retry, a scratch (marked) or Show me, so
 * after a miss the lesson bar offers Show me instead of Next. A spot it marks
 * the word and shows its meaning after a wrong tap or pick; a dictation is
 * typed like a GapFill. A sound-alike marks the right word after any pick; a
 * say-it shows its word once "I said it" or the check has judged it.
 */
export const AFTER_A_MISS: Record<string, 'shown' | 'show-me'> = {
  Flashcard: 'shown',
  Choice: 'shown',
  SayItBack: 'shown',
  SpotIt: 'shown',
  SoundAlike: 'shown',
  SayFromMeaning: 'shown',
  GapFill: 'show-me',
  Reorder: 'show-me',
  Dictation: 'show-me',
};

/**
 * Whether this action leaves the card's answer off the screen: a miss on a
 * GapFill, Reorder or Dictation that was no scratch, no Show me and no give-up
 * ("I don't know — show me", ticket 46: the card shows its answer itself).
 */
export function answerHidden(type: string, action: string, ctx: Record<string, unknown>): boolean {
  if (action !== 'answer' || AFTER_A_MISS[COMPONENT[type as PackTaskType]] !== 'show-me') return false;
  return ctx.correct !== true && ctx.revealed !== true && ctx.scratched !== true && ctx.gaveUp !== true;
}

/**
 * The lesson bar's Show me: the card on screen shows its answer and sends its
 * usual reveal action. False when no such card is on screen.
 */
export function showAnswerOn(view: Element): boolean {
  const card = view.querySelector('a2ui-surface')?.shadowRoot?.querySelector('li-gapfill, li-reorder, li-dictation') as
    (Element & { showAnswer?: () => void }) | null | undefined;
  if (!card?.showAnswer) return false;
  card.showAnswer();
  return true;
}

/**
 * A say-it card (ticket 14) asked by its reading alternative instead: only
 * review can check a word said aloud, so practice, the exam and a review with
 * no check to make ask the same word this way. Its answer settles the word's
 * easier card like the say-it card would.
 */
export function withoutSaying(e: ReviewQueueEntry): ReviewQueueEntry {
  if (e.task.type !== 'sayfrommeaning' || !e.alternative) return e;
  return { ...e, task: e.alternative, alternative: undefined, ...(e.card ? { card: 'recall' as const } : {}) };
}

/** Card types the learner types an answer into (server/src/spelling.ts `TYPED_CARDS`). */
const TYPED_CARDS = new Set(['gapfill', 'recall', 'dictation', 'spelling']);

/**
 * The answer text an attempt carries: always for a typed card, so the server
 * grades it with the spelling grader and keeps it for later re-grading
 * (ticket 05); for other cards only on a miss, for the recap.
 */
export function givenText(type: string, ctx: Record<string, unknown>, correct: boolean): string | undefined {
  if (typeof ctx.value !== 'string') return undefined;
  return !correct || TYPED_CARDS.has(type) ? ctx.value.slice(0, 200) : undefined;
}

/** Catalan line to play aloud for listen tasks (hearing only — never shown as text). */
export function listenAudioText(props: Record<string, unknown> | undefined): string | null {
  const a = props?.audioText;
  return typeof a === 'string' && a ? a : null;
}

/** A wrong answer kept for the end-of-run recap (expected vs given, reused explanations). */
export interface MissNote {
  i: number;
  label: string;
  expected: string;
  explain?: string;
  given?: string;
  disputed?: boolean;
}

/** Fallback expected answer when the card action carries none (choice options, then answer/words, then the English gloss). */
function fallbackExpected(props: Record<string, unknown>): string {
  const options = Array.isArray(props.options) ? (props.options as unknown[]).map(String) : [];
  const idx = Number(props.correct ?? -1);
  if (idx >= 0 && idx < options.length) return options[idx];
  if (typeof props.answer === 'string' && props.answer) return props.answer;
  if (Array.isArray(props.words)) {
    const joined = (props.words as unknown[]).map(String).join(' ');
    if (joined) return joined;
  }
  // Flashcards / SayItBack carry both sides: the recap wants the English
  // meaning (label is the Catalan), never a Catalan→Catalan echo.
  if (typeof props.en === 'string' && props.en) return props.en;
  // Latin transliteration first for script-illiterate learners, script after.
  if (typeof props.latin === 'string' && props.latin) return props.latin;
  return String(props.ca ?? '');
}

/**
 * Short prompt for the recap line. Prefers the Catalan side when the answer
 * is English (flashcard/listen) and the English side when the answer is
 * Catalan (reorder/recall/reply) so label and expected never echo each other.
 */
function recapLabel(props: Record<string, unknown>, itemKey: string): string {
  if (Array.isArray(props.words)) {
    if (typeof props.translation === 'string' && props.translation) return props.translation.slice(0, 60);
    const joined = (props.words as unknown[]).map(String).join(' ');
    if (joined) return joined.slice(0, 40);
  }
  // A dictation's word is its answer: its meaning labels the row, not the word twice.
  if (typeof props.audioText === 'string' && typeof props.translation === 'string' && props.translation) return props.translation.slice(0, 60);
  return String(
    props.latin ?? props.ca ?? props.audioText ?? props.sentence ?? props.question
    ?? props.translation ?? props.en ?? itemKey,
  ).slice(0, 60);
}

/**
 * Remember a miss for the recap; a later correct retry on the same card
 * clears it. Returns the updated list (no mutation).
 */
export function noteMiss(
  misses: MissNote[],
  i: number,
  props: Record<string, unknown>,
  itemKey: string,
  ctx: Record<string, unknown>,
  correct: boolean,
): MissNote[] {
  const expected = String(ctx.expected ?? fallbackExpected(props) ?? '');
  if (correct || !expected) {
    return correct ? misses.filter((m) => m.i !== i) : misses;
  }
  const label = recapLabel(props, itemKey);
  const explain = typeof props.explain === 'string' ? props.explain : undefined;
  const given = typeof ctx.value === 'string' && ctx.value && ctx.value !== expected ? ctx.value.slice(0, 60) : undefined;
  const note: MissNote = { i, label, expected, explain, given };
  const prev = misses.findIndex((m) => m.i === i);
  return prev >= 0
    ? misses.map((m, idx) => (idx === prev ? { ...note, disputed: m.disputed, given: m.given ?? note.given } : m))
    : [...misses, note];
}

/** Short label for "learned this run" toasts. */
export function taskLabel(props: Record<string, unknown>, itemKey: string, len = 30): string {
  return recapLabel(props, itemKey).slice(0, len);
}

/** Skip reasons offered on a card; labels come from `t('skip.<reason>')`. */
/** "Don't know yet" left the list (ticket 46: "I don't know — show me" is a scored miss); the server still reads old `dont_know` skips. */
export const SKIP_REASONS: SkipReason[] = ['defer', 'unclear'];

/** A speech-only reason belongs only on activities that ask the learner to speak. */
export function skipReasonsFor(type: string): SkipReason[] {
  return type === 'sayitback' || type === 'sayfrommeaning' ? SKIP_REASONS : ['unclear'];
}
